import { Copy, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/src/common/components/ui/button";
import { Modal } from "@/src/common/components/ui/modal";

interface McpSetupModalProps {
	provider: "claude" | "chatgpt" | null;
	onOpenChange: (open: boolean) => void;
	serverUrl: string;
	onCopy: () => void | Promise<void>;
}

export function McpSetupModal({ provider, onOpenChange, serverUrl, onCopy }: McpSetupModalProps) {
	const { t } = useTranslation("common");
	const isOpen = provider !== null;

	return (
		<Modal.Backdrop isOpen={isOpen} onOpenChange={onOpenChange}>
			<Modal.Container size="lg" placement="center">
				<Modal.Dialog className="sm:max-w-[640px]">
					<Modal.CloseTrigger />
					<Modal.Header>
						<Modal.Icon className="bg-primary-subtle text-primary">
							<Sparkles className="size-5" aria-hidden />
						</Modal.Icon>
						<Modal.Heading>{provider === "claude" ? t("settings.mcp.claude.install") : t("settings.mcp.chatgpt.install")}</Modal.Heading>
						<div className="mt-3 flex items-center gap-2">
							<code className="flex-1 truncate rounded-xs bg-surface px-2 py-1.5 font-mono text-xs text-foreground">{serverUrl}</code>
							<Button variant="outline" size="sm" onPress={() => void onCopy()}>
								<Copy className="size-3.5" aria-hidden />
								{t("settings.mcp.copy_url")}
							</Button>
						</div>
					</Modal.Header>
					<Modal.Body>
						{provider === "claude" ? (
							<section className="flex flex-col gap-2 text-sm">
								<h3 className="text-sm font-semibold text-foreground">{t("settings.mcp.modal.claude_title")}</h3>
								<ol className="flex flex-col gap-1.5 pl-4 text-muted-foreground [&>li]:list-decimal">
									<li>{t("settings.mcp.modal.claude_step_1")}</li>
									<li>{t("settings.mcp.modal.claude_step_2")}</li>
									<li>{t("settings.mcp.modal.claude_step_3")}</li>
								</ol>
							</section>
						) : (
							<div className="flex flex-col gap-4 text-sm">
								<p className="rounded-s border border-warning bg-warning/10 px-3 py-2 text-xs text-warning-foreground">
									{t("settings.mcp.modal.chatgpt_requirements")}
								</p>
								<ol className="flex flex-col gap-1.5 pl-4 text-muted-foreground [&>li]:list-decimal">
									<li>{t("settings.mcp.modal.chatgpt_step_1")}</li>
									<li>{t("settings.mcp.modal.chatgpt_step_2")}</li>
									<li>{t("settings.mcp.modal.chatgpt_step_3")}</li>
								</ol>
							</div>
						)}
					</Modal.Body>
					<Modal.Footer>
						<Button variant="primary" onPress={() => onOpenChange(false)}>
							{t("settings.mcp.modal.close")}
						</Button>
					</Modal.Footer>
				</Modal.Dialog>
			</Modal.Container>
		</Modal.Backdrop>
	);
}
